/**
 * **상품 질문의 mockup 갈래** — ES 쪽 `actions/els/els-mock` 과 **같은 모양**이다.
 *
 * ```
 *   actions/products/product-bff.ts      질문 첫머리에서 resolveMock*() 한 줄
 *   actions/els/product-rankings.ts      랭킹 인덱스 질문도 같은 줄
 *     │  잡히면 주입본, 아니면
 *     ▼
 *   /api/products/*  ·  /api/els/product-rankings      평소 그대로
 * ```
 *
 * ## ⚠️ 왜 데이터소스 둘이 한 레지스트리인가
 * 상품 정보 조사 화면은 **랭킹 인덱스(ES)와 상품 BFF 를 함께** 묻는다. mockup 은 화면 단위로
 * 켜고 끄는 것이라 등록기를 둘로 쪼개면 한쪽만 켜진 반쪽 화면이 나온다. 그래서 「상품」이라는
 * 도메인으로 묶고, 데이터소스별 경계 파일은 그대로 둔 채 이 레지스트리만 함께 본다.
 *
 * ## ⚠️ 판정은 **「등록됐는가」** 하나다
 * 이 질문들의 params 에는 워크스페이스도 캠페인도 실리지 않는다(상품이 **결과**인 질문이다) —
 * `MOCKUP_` 접두 id 로 가를 자리가 없다. 등록은 상품 mockup 라우트의 layout 이 마운트돼
 * 있는 동안에만 살아 있고 언마운트에 풀리므로, 실화면 호출은 여기 닿지 않는다.
 *
 * ## ⚠️ 타입을 좁혀 둔 이유
 * 핸들러마다 Request/Response 를 경계 타입으로 못박았다. 계약이 바뀌면 **주입 변환기가
 * tsc 에서 운다** — 단언(`as`)으로 헐겁게 만들면 그 경보를 잃는다.
 *
 * ⚠️ 핸들러가 `null` 을 주면 「내 것이 아니다」라는 뜻이고 호출은 평소대로 서버로 간다.
 * 등록이 없으면(기본) 모든 질문이 그대로 나간다 — 실화면 동작 무변.
 */
import type {
  ProductRankingBoardRequest,
  ProductRankingDetailRequest,
  ProductRankingBoardResponse,
  ProductRankingSearchRequest,
  ProductRankingThumbsRequest,
  ProductRankingDetailResponse,
  ProductRankingHistoryRequest,
  ProductRankingSearchResponse,
  ProductRankingThumbsResponse,
  ProductRankingHistoryResponse,
  ProductRankingCrossChannelRequest,
  ProductRankingCrossChannelResponse,
} from 'src/types/product-rankings/product-rankings-ops';

import type {
  AmazonProductGallery,
  LiveProductSearchRequest,
  LiveProductSearchResponse,
  ProductDetailCollectEvent,
} from './product-bff';

/**
 * 상세 수집 스트림의 mockup 답 — 흘릴 이벤트와, 다 흘린 뒤 스트림을 **열어 둘** 시간.
 * `holdMs` 가 있으면 그동안 스트림이 끝나지 않아 화면이 「수집 중」에 머문다(캡처용 진행 장면).
 */
export type ProductDetailCollectMock = { events: ProductDetailCollectEvent[]; holdMs?: number };

export type ProductMockHandlers = {
  /** 판매몰 실시간 검색 — `/api/products/live-search` */
  liveSearch?: (params: LiveProductSearchRequest) => LiveProductSearchResponse | null;
  /** 아마존 단건 대표 컷 — `/api/products/amazon-detail` */
  amazonGallery?: (asin: string) => AmazonProductGallery | null;
  /**
   * 상세 수집 스트림 — `/api/products/collect-detail`.
   * ⚠️ 돌려준 이벤트를 **그 자리에서 순서대로** 흘리고 끝난다. 빈 배열이면 「즉시 완료·결과 없음」이다.
   * `{ events, holdMs }` 로 주면 흘린 뒤 `holdMs` 동안 스트림을 열어 둔다.
   */
  detailCollect?: (items: readonly unknown[]) => ProductDetailCollectEvent[] | ProductDetailCollectMock | null;
  /** 랭킹 DB 검색 (`rankings.search`) — 순위표를 채우는 질문 */
  rankingSearch?: (params: ProductRankingSearchRequest) => ProductRankingSearchResponse | null;
  /** 판매채널 보드 (`rankings.board`) — 검색 전 참고 화면 */
  rankingBoard?: (params: ProductRankingBoardRequest) => ProductRankingBoardResponse | null;
  /**
   * 상품 상세 셋 (`rankings.detail` · `rankings.history` · `rankings.crossChannel`).
   * ⚠️ 이 셋은 `postId` 가 실린다 — 핸들러가 `MOCKUP_` 접두인 것만 잡는다.
   */
  rankingDetail?: (params: ProductRankingDetailRequest) => ProductRankingDetailResponse | null;
  rankingHistory?: (params: ProductRankingHistoryRequest) => ProductRankingHistoryResponse | null;
  crossChannel?: (params: ProductRankingCrossChannelRequest) => ProductRankingCrossChannelResponse | null;
  /** 담아 둔 상품의 얼굴 (`rankings.thumbs`) — 북마크 보드 카드. 핸들러가 `MOCKUP_` 접두인 것만 잡는다 */
  rankingThumbs?: (params: ProductRankingThumbsRequest) => ProductRankingThumbsResponse | null;
};

/** mockup 라우트만 부른다. `null` 이면 해제다 */
export function registerProductMock(next: ProductMockHandlers | null): void {
  handlers = next;
}

export function resolveMockLiveProductSearch(
  params: LiveProductSearchRequest,
): LiveProductSearchResponse | null {
  return handlers?.liveSearch?.(params) ?? null;
}

export function resolveMockAmazonProductGallery(asin: string): AmazonProductGallery | null {
  return handlers?.amazonGallery?.(asin) ?? null;
}

export function resolveMockProductDetailCollect(items: readonly unknown[]): ProductDetailCollectMock | null {
  const answer = handlers?.detailCollect?.(items) ?? null;
  if (!answer) return null;
  return Array.isArray(answer) ? { events: answer } : answer;
}

export function resolveMockProductRankingSearch(
  params: ProductRankingSearchRequest,
): ProductRankingSearchResponse | null {
  return handlers?.rankingSearch?.(params) ?? null;
}

export function resolveMockProductRankingBoard(
  params: ProductRankingBoardRequest,
): ProductRankingBoardResponse | null {
  return handlers?.rankingBoard?.(params) ?? null;
}

export function resolveMockProductRankingDetail(
  params: ProductRankingDetailRequest,
): ProductRankingDetailResponse | null {
  return handlers?.rankingDetail?.(params) ?? null;
}

export function resolveMockProductRankingHistory(
  params: ProductRankingHistoryRequest,
): ProductRankingHistoryResponse | null {
  return handlers?.rankingHistory?.(params) ?? null;
}

export function resolveMockProductCrossChannel(
  params: ProductRankingCrossChannelRequest,
): ProductRankingCrossChannelResponse | null {
  return handlers?.crossChannel?.(params) ?? null;
}

export function resolveMockProductRankingThumbs(
  params: ProductRankingThumbsRequest,
): ProductRankingThumbsResponse | null {
  return handlers?.rankingThumbs?.(params) ?? null;
}

let handlers: ProductMockHandlers | null = null;
